import React, { PureComponent } from 'react';
import Helmet from 'react-helmet';
import objectPath from 'object-path';
import apiData from 'Lib/apiData';
import formatMoney from 'Lib/formatMoney';
import makePercent from 'Lib/makePercent';
import commaNumber from 'Lib/commaNumber';
import formatImpact from 'Lib/formatImpact';
import ProgramHero from '../../components/program/program-hero';
import ProgramProgress from '../../components/program/program-progress';
import ProgramOverview from '../../components/program/program-overview';
import ProgramCarousel from '../../components/program/program-carousel';
import ProgramSupport from '../../components/program/program-support';
import ProgramStartFundraiser from '../../components/program/program-start-fundraiser';

const fetchRelated = async (data) => {
  if (!data || (data && !data.supportingFundraisersIds)) return;

  const related = data.supportingFundraisersIds.join(',');
  const relatedList = await apiData('/v1/fundraisers', { ids: related });

  return relatedList.items;
};

export default class Program extends PureComponent {

  static async getInitialProps({ query }) {
    const { projectId } = query;

    const data = await apiData(`/v1/programs/${projectId}`);

    const related = await fetchRelated(data);

    return { data, projectId, related };
  }

  formatStats = (data, id = null) => {
    const rawFunds = (data.fundsRaised && data.fundsRaised.raw) || 0;
    const rawGoal = (data.fields.goal && data.fields.goal.raw) || 0;

    if (`${id}` === process.env.TUMI_PROGRAM) {
      const { totalDonations, startingAmount } = data;

      const moneyRaised = new Intl.NumberFormat(
        'en-EN',
        {
          style: 'currency',
          currency: 'EUR',
          minimumFractionDigits: 0,
          maximumFractionDigits: 0,
        },
      ).format((totalDonations * 50) + Math.ceil(startingAmount.raw * 0.8333));

      return [
        {
          value: moneyRaised,
          title: 'Money Raised (EUR)',
        },
        {
          value: Math.ceil(totalDonations + (startingAmount.raw / 60)),
          title: 'Filters Raised',
        },
        {
          value: commaNumber(formatImpact((rawFunds + 10), 60)),
          title: 'People Impacted',
        },
      ];
    }

    return [
      {
        value: `${formatMoney(Math.ceil(rawFunds))} / ${(formatMoney(rawGoal))}`,
        title: `Money Raised (${data.billing && data.billing.settlementCurrency})`,
      },
      {
        value: rawGoal - rawFunds < 0 ? '$0' : formatMoney(Math.ceil(rawGoal - rawFunds)),
        title: 'Remaining to Goal',
      },
      {
        value: rawFunds / rawGoal > 1 ? '100%' : makePercent(rawFunds, rawGoal, true),
        title: 'Percent Funded',
      },
      {
        value: commaNumber(formatImpact(rawFunds, 50)),
        title: 'People Impacted',
      },
    ];
  };

  render() {
    const { data, related } = this.props;
    const { seo } = data.fields;
    const todayDate = new Date();
    const featuredImage = objectPath.get(data, 'fields.featuredImage.photo');
    const ogImage = objectPath.get(data.fields, 'seo.fields.openGraphImage.fields.file', featuredImage && featuredImage.file);

    return (
      <main>
        <Helmet>
          <title>{data.title}</title>
          <meta property="og:title" content={data.title} />
          <meta name="twitter:title" content={data.title} />
          {ogImage && <meta property="og:image" content={`https:${ogImage.url}?w=1200&h=630&fit=thumb&f=center`} />}
          {ogImage && <meta name="twitter:image" content={`https:${ogImage.url}?w=1200&h=600&fit=thumb&f=center`} />}
          {objectPath.has(seo, 'fields.pageDescription') && <meta name="description" content={seo.fields.pageDescription} />}
          {objectPath.has(seo, 'fields.openGraphDescription') && <meta property="og:description" content={seo.fields.openGraphDescription} />}
          {objectPath.has(seo, 'fields.openGraphDescription') && <meta name="twitter:description" content={seo.fields.openGraphDescription} />}
        </Helmet>
        <ProgramHero
          subheading="Waves For Water Program"
          title={data.fields.title}
          location={data.fields.location}
          image={featuredImage}
        />
        <ProgramProgress
          stats={this.formatStats(data, this.props.projectId)}
          currency={data.settlementCurrency}
          id={this.props.projectId}
          showDonation={data.status === 'published' && (!data.endDate || new Date(data.endDate) > todayDate)}
        />
        <ProgramOverview
          content={data.fields.description}
          programTitle={data.fields.title}
        />
        <ProgramCarousel
          slides={data.fields.images}
        />
        <ProgramSupport
          related={related}
          overview={data.fields.overview}
        />
        <ProgramStartFundraiser />
      </main>
    );
  }
}
